'use client';

import { useRouter } from 'next/navigation';
import type { MapRef } from 'react-map-gl/mapbox';
import { type SearchPageData } from '@/app/search/createSearchPageData.ts';
import NavigationManager from '@/app/common/components/NavigationManager';
import { PAGE_TYPE, RESULTS_STEP_PAGE_TYPES, showsCsw } from '@/app/common/pageType.ts';
import { isEvoLayout, isEvoResultsLayout } from '@/app/common/evoLayout.ts';
import React, { startTransition, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { type BrandedProps } from '@/app/hotel-detail/createHotelDetailData.ts';
import CommonClientProvider, { type CommonClient } from '@/app/common/commonClientProvider.tsx';
import { isListView, type ListViewType } from '@/app/common/viewModeUtils.ts';
import PriceModeProvider from '@/app/common/priceModeProvider.tsx';
import { useIsDesktop, useIsMobile, useIsTablet } from '@/app/components/search/utils/useSsrMedia.ts';
import type { PageWrapperData } from '@/app/common/createPageWrapperData.ts';
import { fetchPageWrapperData } from '@/app/common/refreshPageWrapperData.ts';
import { createLiveHandler } from '@/app/common/liveHandler.ts';
import PageWrapperProvider from '@/app/common/pageWrapperProvider.tsx';
import { isEmptyObject } from '@/app/components/search/Utilities.ts';
import { createSearchParamsFromUrl } from '@/app/common/createSearchParamsFromUrl.ts';
import { useLatchedTrue } from '@/app/common/hooks/useLatchedTrue.ts';
import getSearchParams from '@/app/components/search/common/getSearchParams.ts';
import {
  getPageTypeFromSearchParams,
  getSearchParamsFromQueryString,
  updateUrl,
} from '@/app/common/urlParametersUtils.ts';
import { type SearchParams } from '@/server/searchParams.ts';
import { URLParameters } from '@/app/common/urlParameters.ts';
import { captureInitialModalStack } from '@/app/common/modalStack.ts';
import { createHandleUserChangedLanguage, updateCommonDataByDevice } from '@/app/common/PageWrapperClientUtils.tsx';
import CuriosityManagerProvider from '@/app/common/CuriosityManagerProvider.tsx';
import { APIStatusEnum } from '@/app/components/search/common/reloadData/APIStatusEnum.ts';
import type { CSWProps, DefaultValuesCSW, SupportedTabId } from '@/app/common/CSW/services/types';
import getDefaultValues from '@/app/common/CSW/services/getDefaultValues';
import type { SearchInfoDetailData } from '@/app/search/types.ts';
import { SEARCH_TYPE } from '@/app/search/types.ts';
import SearchDefaultDataProvider from '@/app/components/search/searchDefaultDataProvider.tsx';
import { TsxContentHubFooter } from '@/app/common/styled.ts';
import dynamic from 'next/dynamic';
import { createStarTrack } from '@/app/common/starTrack/startrack.ts';
import { StyleSheetManagerProvider } from '@lmn/design-system';
import { getDateWithoutTimeFromYYYYMMDD } from '@/app/components/search/utils/dates/getDateWithoutTime.ts';
import type { DefaultValues } from '@/app/components/search/common/getDefaultValues.ts';
import { isLiveSearchByDate } from '@/app/components/search/utils/api/isLiveSearchByDate.ts';
import { isValidHotelId } from '@/app/components/hotel-detail/common/common.helper.ts';
import getCSWTabId from '@/app/common/CSW/services/adapters/getCSWTabId.ts';
import { useTranslation } from '@/app/components/translations/Translation.tsx';
import { dynamicImportCatch } from '@/utils/appUtilsClient.tsx';
import { canUseDOM } from '@/app/common/utils/canUseDOM';
import {
  shouldTrackAdditionalParams,
  createSearchAdditionalParameterPayload,
} from '@/app/common/starTrack/events/searchAdditionalParameter.ts';
import ErrorBoundary from '@/app/common/components/ErrorBoundary';
import {
  createPageWrapperClientSearch,
  handleNewSearchLive,
  publishHandleNewSearch,
  publishSetDefaultValuesCSW,
  setDefaultValuesCSWLive,
  unpublishHandleNewSearch,
  unpublishSetDefaultValuesCSW,
} from '@/app/common/pageWrapperClientSearch.ts';
import { waitingCall } from '@/app/components/search/common/waitingCall.ts';
import { VersionGuard } from '@/app/common/VersionGuard.tsx';
import { BiffBootstrap } from '@/app/common/biff/BiffBootstrap.tsx';
import { ClientSideCasperCookieSetter } from '@/app/common/cookies/ClientSideCookiesSetter.tsx';
import { DebugUserIdLog } from '@/app/components/common/DebugUserId.tsx';
import Script from 'next/script';
import { clientAppLog } from '@/app/components/search/logging/clientAppLog.ts';
import { fullPageReloadWithDelay } from '@/utils/reload.ts';
import { UBUNTU_FONT_STACK } from '@/utils/ubuntuFont';
import useInitialScrollbarWidth from '@/app/common/hooks/useInitialScrollbarWidth.ts';
import { trackingSearchRequested } from '@/app/common/tracking/trackingSearchRequested.ts';
import { amplitudeIdentify } from '@/app/common/amplitude/amplitudeIdentify.ts';
import { amplitudeInit } from '@/app/common/amplitude/amplitudeInit.ts';
import { getActiveABTests } from '@/app/common/ABTesting/abTestsUtils.ts';
import { canInitialiseAmplitude, canUseAmplitude } from '@/app/common/amplitude/amplitudeUtils.ts';
import { createPageWrapperClientDetails } from '@/app/common/pageWrapperClientDetails.ts';
import type { PageWrapperClientProps } from '@/app/common/PageWrapperClientTypes.tsx';
import { scrollToTop } from '@/app/common/utils/scroll.ts';
import {
  setSearchLoadingStageIfUnnamed,
  watchSearchStranded,
} from '@/app/components/search/logging/searchLoadingWatchdog.ts';
import BrandedPage from '../components/hotel-detail/BrandedPage';
import HotelAndRoomsLayout from '../components/hotel-detail/HotelAndRoomsLayout';
import EvoDetailsLayout from '../components/hotel-detail/evo/EvoDetailsLayout';
import SearchMain from '../components/search/SearchMain';
import { Context, Platform } from './starTrack/events/utils/starTrack.types';
import { createGeneralPageTitle, setPageTitle } from './utils/pageTitles';

const TransportLayout = dynamic(
  () => import('@/app/components/hotel-detail/TransportLayout.tsx').catch(dynamicImportCatch),
  {
    ssr: true,
  }
);
const EvoTransportLayout = dynamic(
  () => import('@/app/components/hotel-detail/evo/EvoTransportLayout').catch(dynamicImportCatch),
  { ssr: true }
);
const PackageReviewLayout = dynamic(
  () => import('@/app/components/hotel-detail/package-review/PackageReviewLayout').catch(dynamicImportCatch),
  {
    ssr: true,
  }
);
// Import the Footer dynamically as it was blocking the loading of the page due to hydration errors - it shouldn't need to be
// loaded later on as it is always visible so shouldn't cause a problem between builds.
const ContentHubFooter = dynamic(() => import('@/app/common/ContentHubFooter.tsx'));

const DETAIL_PAGE_TYPES = [PAGE_TYPE.HOTEL_DETAIL, PAGE_TYPE.TRANSPORT_DETAIL, PAGE_TYPE.REVIEW];

const getCalendar = (departureIntervals: string) => {
  const [startInterval, endInterval] = departureIntervals.split('-');
  return {
    startDate: getDateWithoutTimeFromYYYYMMDD(startInterval),
    endDate: getDateWithoutTimeFromYYYYMMDD(endInterval),
  };
};

export type CreateUpdatedDefaultValuesProps = (
  defaultValues: DefaultValues,
  searchInfoDetailData: SearchInfoDetailData,
  pageType: PAGE_TYPE,
  searchParams: SearchParams
) => DefaultValuesCSW;
// The one rule for the departure intervals of the widget: a detail step shows the exact dates of
// the hotel (the departureIntervals URL param), a results step shows the dates of the search as the
// default values carry them, flexible or exact, so the param of a deeplink is not read there. The
// page type and the search params must belong to the page the widget is built for: on a browser
// navigation the page data in state can still be the previous step's.
const createUpdatedDefaultValues: CreateUpdatedDefaultValuesProps = (
  defaultValues,
  searchInfoDetailData,
  pageType,
  searchParams
): DefaultValuesCSW =>
  getDefaultValues({
    searchInfoDetailData,
    defaultDates: defaultValues.defaultDates,
    defaultPeopleData: defaultValues.defaultPeopleData,
    defaultFlightClass: defaultValues.defaultFlightClass,
    defaultStayDates: defaultValues.defaultStayDates,
    departureIntervals: RESULTS_STEP_PAGE_TYPES.includes(pageType) ? undefined : searchParams[URLParameters.TRIP_DATES],
  });

const stepsRendered = new Set();

// The window listeners are registered once, on mount, so they would keep the handlers of the mount
// render and read the state of that render for ever. They go through a live handler instead: each
// committed render publishes its own handler, so a browser navigation or a login acts on the state
// the page holds now (see liveHandler.ts).
const popStateLive = createLiveHandler<() => void>('windowPopState');
const userLoggedInLive = createLiveHandler<() => Promise<void>>('handleUserLoggedIn');

export const PageWrapperClient = ({
  contentHubFooterProps,
  contentHubHeaderProps,
  ssrUtilsCSW,
  hotelDetailData: hotelDetailDataProp,
  hotelId: hotelIdProp,
  transportId,
  pageType: pageTypeProp,
  pageWrapperData: pageWrapperDataProp,
  searchPageData: searchPageDataProp,
  searchDefaultData,
  externalHeaderEnabled,
  externalHeaderHTML,
  headerFooterStaticEnabled,
}: PageWrapperClientProps) => {
  const [pageType, setPageType] = useState<PAGE_TYPE>(pageTypeProp);
  const [hotelId, setHotelId] = useState<string>(hotelIdProp || '');
  const [searchPageData, setSearchPageData] = useState<SearchPageData>(searchPageDataProp);
  const [pageWrapperData, setPageWrapperData] = useState<PageWrapperData>(pageWrapperDataProp);
  const [hotelDetailData, setHotelDetailData] = useState<BrandedProps>(hotelDetailDataProp);
  const scrollPosSearch = useRef<number>(0);
  const hotelIdRef = useRef<string>(hotelIdProp || '');
  const mealPlanIdRef = useRef<string | undefined>(undefined);
  const mealPlanCodeRef = useRef<string | undefined>(undefined);
  const brandedFareIdRef = useRef<string | undefined>(undefined);
  const mapRef = useRef<MapRef>(null);
  // Seeded from the view mode resolved for this search; the results page reports every toggle on top.
  const listViewRef = useRef<ListViewType>(null);
  // False for the mount sync only, which is the one WAIT snapshot the effect below does poll.
  const hasSyncedSearchPageDataProp = useRef<boolean>(false);
  const router = useRouter();
  const { t, translationDictionary } = useTranslation();
  useInitialScrollbarWidth();

  const { searchInfoData, searchInfoDetailData, staticData, commonData, localization, curiosityParams } =
    pageWrapperData;
  const { isLocal } = curiosityParams;
  const { isApp } = commonData;
  const { searchId, searchMode } = searchInfoData;
  const { brand } = localization;
  const accommodationOnly = searchMode === 'HO';
  const reviewDesktop = pageType === PAGE_TYPE.REVIEW && commonData.isDesktopDevice;
  const evolutionEnabled = localization?.configValuesApp?.evolutionEnabled;
  const isEvoStep = isEvoLayout(pageType, localization?.configValuesApp);
  const isEvoResultsStep = isEvoResultsLayout(pageType, localization?.configValuesApp);
  const isEvoDetailStep =
    evolutionEnabled && (pageType === PAGE_TYPE.HOTEL_DETAIL || pageType === PAGE_TYPE.TRANSPORT_DETAIL);
  const showNavigation = !accommodationOnly && !reviewDesktop && !isEvoStep;
  const showGlobalLoadLine = isEvoStep && !showNavigation && !externalHeaderEnabled;
  const generalPageTitle =
    pageType === PAGE_TYPE.HOTEL_DETAIL
      ? localization?.pageTitle
      : createGeneralPageTitle({ searchInfoData, searchInfoDetailData, t, defaultTitle: localization?.pageTitle });

  const updatedCommonData = updateCommonDataByDevice(commonData);
  const updatedPageWrapperData = { ...pageWrapperData, commonData: updatedCommonData };
  const { isMobileDevice, isDesktopDevice, isTabletDevice } = updatedCommonData;
  const isMobile = useIsMobile(isMobileDevice);
  const isTablet = useIsTablet(isTabletDevice);
  const isDesktop = useIsDesktop(isDesktopDevice);
  const { viewMode } = pageWrapperData;
  listViewRef.current = listViewRef.current ?? isListView(isDesktop ? viewMode.desktop : viewMode.mobile);
  const reportListView = useCallback((listView: ListViewType) => {
    listViewRef.current = listView;
  }, []);
  const isDebug = pageWrapperData.searchParams[URLParameters.DEBUG] === 'true';
  // On the evo detail steps (hotel details and flights) the header with the CSW scrolls away
  // with the page instead of staying fixed at the top.
  const staticHeader = isEvoDetailStep;
  // Latched for the life of the document: the flag is an experiment decision, and every
  // client-side refetch of the config re-resolves it, so one request that cannot reach SAAB
  // would withdraw the whole app header from a session that already had it. (MPH-1123)
  const appShowCsw = useLatchedTrue(!!localization?.configValuesApp?.appShowCsw);
  const debugData = isDebug
    ? {
        abTestsActive: getActiveABTests({ abTests: commonData.abTests, searchParams: pageWrapperData.searchParams }),
        ...pageWrapperData,
        productsData: searchPageData.productsData,
        filtersGroups: searchPageData?.filtersGroups,
        contentHubHeaderProps,
        contentHubFooterProps,
      }
    : {};

  const starTrack = useMemo(
    () =>
      createStarTrack({
        localization,
        searchInfoData,
        commonData,
      }),
    [localization, searchInfoData, commonData]
  );

  // Computed once, for the page the widget mounts on. After that only the setter moves the widget:
  // the refresh in handleSearchChangedBase, the submit handlers, the details date sync, the seed effect.
  const [defaultValuesCSW, setDefaultValuesCSW] = useState(() =>
    createUpdatedDefaultValues(
      searchDefaultData.defaultValues,
      searchInfoDetailData,
      pageType,
      pageWrapperData.searchParams
    )
  );

  const pageWrapperClientSearch = createPageWrapperClientSearch({
    commonData,
    pageWrapperData,
    searchPageData,
    searchDefaultData,
    localization,
    scrollPosSearch,
    mapRef,
    isMobile,
    translationDictionary,
    starTrack,
    createUpdatedDefaultValues,
    scrollToTop,
    setDefaultValuesCSW,
    setPageType,
    setPageWrapperData,
    setSearchPageData,
    setHotelId,
    getListView: () => listViewRef.current,
  });

  const pageWrapperClientDetails = createPageWrapperClientDetails({
    commonData,
    localization,
    pageWrapperData,
    searchPageData,
    searchInfoData,
    searchInfoDetailData,
    pageType,
    hotelDetailData,
    hotelId,
    hotelIdRef,
    transportId,
    accommodationOnly,
    mealPlanIdRef,
    mealPlanCodeRef,
    brandedFareIdRef,
    scrollPosSearch,
    pageWrapperClientSearch,
    starTrack,
    setHotelDetailData,
    setHotelId,
    setPageType,
    setPageWrapperData,
    setDefaultValuesCSW,
  });

  // No dependency array on purpose: an effect only runs for a committed render, so this always publishes
  // the handler of the instance that is actually mounted. The cleanup takes it back, so nothing keeps
  // a handler of an instance that is gone.
  useEffect(() => {
    const { handleNewSearch } = pageWrapperClientSearch;
    publishHandleNewSearch(handleNewSearch);
    return () => unpublishHandleNewSearch(handleNewSearch);
  });

  // The setter identity is stable, so this runs once for each mounted instance.
  useEffect(() => {
    publishSetDefaultValuesCSW(setDefaultValuesCSW);
    return () => unpublishSetDefaultValuesCSW(setDefaultValuesCSW);
  }, [setDefaultValuesCSW]);

  // Sync fresh server-side searchPageData prop into local state so that client effects
  // (e.g. the Dynamic Filters call in SearchMain) fire only after the server has finished
  // createSearchPageData and the resulting productsData has reached the client.
  useEffect(() => {
    // A snapshot arriving in WAIT raises both loading flags, so it is an arming path of its own — name it
    // or the stuck report ships an empty breadcrumb. Unnamed and past the first sync also means nothing
    // is polling behind it: no client search is being measured, and the mount-only effect below has run.
    if (searchPageDataProp?.productsData?.apiStatus === APIStatusEnum.WAIT) {
      const startedThisStretch = setSearchLoadingStageIfUnnamed('serverWait');
      if (startedThisStretch && hasSyncedSearchPageDataProp.current) {
        watchSearchStranded();
      }
    }
    hasSyncedSearchPageDataProp.current = true;
    setSearchPageData(searchPageDataProp);
  }, [searchPageDataProp]);

  useEffect(() => {
    if (generalPageTitle) {
      setPageTitle(generalPageTitle);
    }
  }, [generalPageTitle]);

  const goToPage = async (
    pageTypeCurrentPage: PAGE_TYPE,
    pageTypeNextPage: PAGE_TYPE,
    shouldAddBrowserHistory: boolean,
    isPopState = false
  ) => {
    const searchParamsNextPage = getSearchParams();

    if (pageTypeNextPage !== PAGE_TYPE.TRANSPORT_DETAIL || !searchParamsNextPage[URLParameters.MODAL]) {
      // navigating back from package review to flight details we want to show the previously selected branded fare
      // but after closing the modal we reset the branded fare id to always show the default (cheapest).
      brandedFareIdRef.current = undefined;
    }

    if (pageTypeNextPage === PAGE_TYPE.SEARCH || pageTypeNextPage === PAGE_TYPE.SEARCH_DESTINATIONS) {
      const isSearchDifferent =
        pageTypeCurrentPage === PAGE_TYPE.SEARCH || pageTypeCurrentPage === PAGE_TYPE.SEARCH_DESTINATIONS;

      if (isEmptyObject(searchPageData?.productsData)) {
        pageWrapperClientSearch.handleIsLoadingChanged(true);
      }
      setPageType(pageTypeNextPage);

      // handleSearchChangedBase refreshes the search widget defaults (refreshCSW defaults to true),
      // so no separate refresh is needed here.
      pageWrapperClientSearch.handleSearchChangedBase({
        refreshSearch: isSearchDifferent,
        shouldAddBrowserHistory,
        scrollPos: isSearchDifferent ? 0 : undefined,
        // Popstate passes none: the restored URL already carries the highlight the list was built
        // with, and createResultsParamsToDrop keeps it for a browser back. The breadcrumb arrow
        // (handleGoToNextStep) passes the hotel just left, so the list keeps pinning it.
        highlightedHotel: isPopState ? undefined : hotelDetailData?.initialParams?.internalHotelId,
        isBrowserBackNavigation: true,
      });
    } else if (pageTypeNextPage === PAGE_TYPE.HOTEL_DETAIL) {
      const hotelIdNextPageSplit = window.location.pathname.split('/');
      const hotelIdNextPage = hotelIdNextPageSplit[hotelIdNextPageSplit.length - 1];
      pageWrapperClientDetails.handleHotelChosenBase(hotelIdNextPage, { shouldAddBrowserHistory });
    } else if (pageTypeNextPage === PAGE_TYPE.TRANSPORT_DETAIL) {
      const searchParams: SearchParams = getSearchParamsFromQueryString(window.location.search);
      pageWrapperClientDetails.handleRoomChosenBase({
        roomOfferId: searchParams[URLParameters.ROOM_ID],
        mealPlanId: searchParams[URLParameters.MEALPLAN_ID],
        mealPlanCode: searchParams[URLParameters.MEALPLAN_CODE],
        shouldAddBrowserHistory,
        isBreadcrumb: true,
      });
    } else if (pageTypeNextPage === PAGE_TYPE.REVIEW) {
      const searchParams: SearchParams = getSearchParamsFromQueryString(window.location.search);
      pageWrapperClientDetails.handleTransportChosenBase({
        transportIdChosen: searchParams[URLParameters.TRANSPORT_ID],
        shouldAddBrowserHistory,
      });
    }
  };

  const handleGoToNextStep = (pageTypeNextPage: PAGE_TYPE) => {
    const searchParamsFresh = getSearchParams();
    const pageTypeCurrentPage = getPageTypeFromSearchParams(searchParamsFresh);
    goToPage(pageTypeCurrentPage, pageTypeNextPage, true);
  };

  const handleUserLoggedIn = async () => {
    if (isLocal) return;
    if (!commonData?.authResponse?.loggedIn) {
      if (canUseAmplitude(commonData, localization)) {
        amplitudeIdentify(true, commonData);
      }

      const searchParamsFresh = getSearchParams();
      if (pageType === PAGE_TYPE.SEARCH || pageType === PAGE_TYPE.SEARCH_DESTINATIONS) {
        pageWrapperClientSearch.handleIsLoadingChanged(true);
        pageWrapperClientSearch.handleSearchChanged({ newSearchParams: searchParamsFresh, keepPage: true });
      } else {
        const pageWrapperDataUpdated = await fetchPageWrapperData({
          searchParams: searchParamsFresh,
          traceParent: searchInfoData.searchTraceParent,
          commonData,
        });

        if (!pageWrapperDataUpdated) {
          clientAppLog({
            level: 'WARN',
            codeSourceName: 'pageWrapperClient handleUserLoggedIn',
            logMessage: 'Failed to refresh PageWrapperData after login, the page keeps the current data.',
            url: window.location.href,
          });
          return;
        }

        setPageWrapperData(pageWrapperDataUpdated);
      }
    }
  };

  const handleUserLoggedOut = () => {
    fullPageReloadWithDelay(1000);
  };

  /**
   * React Components Desynchronization when navigating back using browser history.
   *
   * Description:
   * - When users navigate through the steps of the application, the page loads correctly.
   * - However, if the user:
   *   1. Refreshes the page, or
   *   2. Goes back from the checkout page (equivalent to a page refresh),
   *   the page loads correctly, but React components become desynchronized when using browser
   *   backward navigation.
   *
   * Observed Behavior:
   * - When navigating back using the browser's navigation (e.g., back button),
   *   React components become desynchronized
   * - This desynchronization causes React components to not re-render
   *   even when the state changes due to JavaScript events like `popstate`. This could be
   *   caused by the browser not refreshing the page automatically because is the same URL
   *   path for the different steps.
   *
   * Resolution:
   * - Calling `router.refresh()` resolves the issue by rehydrating the page
   *   and synchronizing React components with the updated state.
   *
   * Implementation:
   * - Store all steps rendered during the current navigation.
   * - If the user navigates back to a step that hasn't been rendered yet,
   *   trigger `router.refresh()` to ensure proper synchronization.
   */
  const windowPopState = () => {
    const searchParamsNextPage = getSearchParams();
    const pageTypeNextPage = getPageTypeFromSearchParams(searchParamsNextPage);

    const callbackFn = () => {
      if (pageTypeNextPage !== pageType) {
        goToPage(pageType, pageTypeNextPage, false, true);
      } else if (pageTypeNextPage === PAGE_TYPE.SEARCH || pageTypeNextPage === PAGE_TYPE.SEARCH_DESTINATIONS) {
        goToPage(pageType, pageTypeNextPage, false, true);
      } else if (pageTypeNextPage === PAGE_TYPE.HOTEL_DETAIL) {
        const hotelIdNextPageSplit = window.location.pathname.split('/');
        const hotelIdNextPage = hotelIdNextPageSplit[hotelIdNextPageSplit.length - 1];
        const internalHotelIdNextPage = Number(hotelIdNextPage);
        if (hotelIdNextPage !== hotelId && isValidHotelId(internalHotelIdNextPage)) {
          setHotelDetailData((prev) => ({
            ...prev,
            initialParams: { ...prev.initialParams, internalHotelId: internalHotelIdNextPage },
          }));
          setHotelId(hotelIdNextPage);
          scrollToTop();
        }
      }
    };

    if (!stepsRendered.has(pageTypeNextPage)) {
      if (!externalHeaderEnabled) {
        setTimeout(() => {
          router.refresh();
          setTimeout(() => callbackFn(), 100);
        }, 1);
      }
    } else {
      callbackFn();
    }

    // TODO: verify that is working correctly on devices
    // console.log('aaa csw:popstate', window.location.search);
    // if (event?.state?.modal) {
    // blockScroll();
    // } else {
    // resetScrollBlock();
    // }
  };

  // No dependency array on purpose, as for handleNewSearch above: every committed render publishes
  // the handlers the window listeners delegate to, so they act on the state of this render.
  useEffect(() => {
    popStateLive.publish(windowPopState);
    userLoggedInLive.publish(handleUserLoggedIn);
    return () => {
      popStateLive.unpublish(windowPopState);
      userLoggedInLive.unpublish(handleUserLoggedIn);
    };
  });

  useEffect(() => {
    if (pageType !== PAGE_TYPE.SEARCH && pageType !== PAGE_TYPE.SEARCH_DESTINATIONS) {
      scrollToTop();
    }
  }, [pageType]);

  useEffect(() => {
    hotelIdRef.current = hotelId;
  }, [hotelId]);

  useEffect(() => {
    if (canInitialiseAmplitude(commonData, localization)) {
      amplitudeInit(staticData);
    }
    if (canUseAmplitude(commonData, localization)) {
      amplitudeIdentify(commonData.authResponse.loggedIn, commonData);
    }

    const searchParams = getSearchParamsFromQueryString(window.location.search);

    if (searchParams[URLParameters.MEALPLAN_CODE]) {
      mealPlanCodeRef.current = searchParams[URLParameters.MEALPLAN_CODE];
    }
    if (searchParams[URLParameters.MEALPLAN_ID]) {
      mealPlanIdRef.current = searchParams[URLParameters.MEALPLAN_ID];
    }
    if (searchParams[URLParameters.BRANDED_FARE_ID]) {
      brandedFareIdRef.current = searchParams[URLParameters.BRANDED_FARE_ID];
    }

    const shouldUpdateSearchId = searchId !== searchParams[URLParameters.SEARCH_ID];

    const queryParamsToSet = [];
    const queryParamsToRemove = [];
    if (shouldUpdateSearchId) {
      clientAppLog({
        level: 'INFO',
        codeSourceName: 'pageWrapperClient',
        logMessage: `REF999 searchId updated. searchId:${searchId}, searchParamsSearchId:${searchParams[URLParameters.SEARCH_ID]}`,
        url: window?.location?.href,
      });
      queryParamsToSet.push({ [URLParameters.SEARCH_ID as string]: searchId });
    }

    if (commonData.casperResponseProperties?.userAttributes?.seed) {
      queryParamsToSet.push({ [URLParameters.SEED]: commonData.casperResponseProperties.userAttributes.seed });
    }

    if (searchParams[URLParameters.MODAL]) {
      // MPH-985: hand the stack over before stripping it, so a modal provider that mounts after
      // this effect can still reopen the modal the page was loaded with. Whichever of the two runs
      // first captures the stack and the other one is a no-op, which is what takes the ordering
      // between this strip and the restore out of the picture.
      captureInitialModalStack();
      queryParamsToRemove.push(URLParameters.MODAL);
    }

    // Keep the APEX price-absorption params (rapex/rctx/rsign) in the URL on detail pages so a refresh
    // re-sends them to the pricing endpoint and the price stays consistent. They are cleared when the user
    // navigates back to results (see handleSearchChangedBase) — and rctx/rsign also on any detail-page date
    // change, since the context only prices the arrival stay (MPH-904); here we just strip any that leaked
    // into a results-page URL. (MPH-634)
    if (!DETAIL_PAGE_TYPES.includes(pageType)) {
      if (searchParams[URLParameters.RATE_APEX]) {
        queryParamsToRemove.push(URLParameters.RATE_APEX);
      }

      if (searchParams[URLParameters.RATE_PRICE_CONTEXT]) {
        queryParamsToRemove.push(URLParameters.RATE_PRICE_CONTEXT);
      }

      if (searchParams[URLParameters.RATE_PRICE_CONTEXT_SIGNATURE]) {
        queryParamsToRemove.push(URLParameters.RATE_PRICE_CONTEXT_SIGNATURE);
      }
    }

    if (queryParamsToRemove.length || queryParamsToSet.length) {
      updateUrl({ queryParamsToRemove, queryParamsToSet });
    }

    const searchParamsFresh = getSearchParamsFromQueryString(window.location.search);

    // Enforce the correct pageType url parameter for search
    if (
      !searchParams[URLParameters.PAGE_TYPE] ||
      searchParams[URLParameters.PAGE_TYPE] === PAGE_TYPE.SEARCH_DESTINATIONS ||
      searchParams[URLParameters.PAGE_TYPE] === PAGE_TYPE.SEARCH
    ) {
      const expectedPageType =
        searchInfoData.searchType === SEARCH_TYPE.DESTINATIONS ? PAGE_TYPE.SEARCH_DESTINATIONS : PAGE_TYPE.SEARCH;
      if (searchParams[URLParameters.PAGE_TYPE] !== expectedPageType) {
        updateUrl({ queryParamsToSet: [{ [URLParameters.PAGE_TYPE as string]: expectedPageType }] });
      }

      pageWrapperClientSearch.fireSearchEventsBefore({
        searchParams,
        searchInfoData,
        searchInfoDetailData,
        localization,
        commonData,
        curiosityParams,
      });

      pageWrapperClientSearch.fireSearchEventsAfter({
        isNewSearch: true,
        searchParams: searchParamsFresh,
        productsData: searchPageData.productsData,
        searchInfoData,
        searchInfoDetailData,
        defaultDestItems: searchDefaultData.defaultDestItems,
        commonData,
        localization,
        curiosityParams,
      });
    }

    if (
      shouldUpdateSearchId &&
      [PAGE_TYPE.HOTEL_DETAIL, PAGE_TYPE.TRANSPORT_DETAIL, PAGE_TYPE.REVIEW].includes(pageType)
    ) {
      if (shouldTrackAdditionalParams(searchParamsFresh)) {
        starTrack.searchAdditionalParameter(
          createSearchAdditionalParameterPayload({ searchId, searchParams: searchParamsFresh })
        );
      }
      trackingSearchRequested({
        starTrack,
        searchId,
        searchParams: searchParamsFresh,
        searchInfoData,
        searchInfoDetailData,
        commonData,
        localization,
        context: Context.hotelDetail,
        internalHotelId: hotelId,
        platform: Platform.tsx,
        logText: '444 useEffect',
        apexPerimeterFlag: localization.configValuesApp.apexEnabledForResults,
      });
    }

    const handleUserChangedLanguage = (e: Event) =>
      createHandleUserChangedLanguage({ searchInfoData })((e as CustomEvent).detail);

    window.addEventListener('popstate', popStateLive.live);
    window.addEventListener('lmn:login:successful', userLoggedInLive.live);
    window.addEventListener('lmn:checkAuth:successful', userLoggedInLive.live);
    window.addEventListener('lmn:logout:successful', handleUserLoggedOut);
    window.addEventListener('lmn:user:locale:update', handleUserChangedLanguage);

    return () => {
      window.removeEventListener('popstate', popStateLive.live);
      window.removeEventListener('lmn:login:successful', userLoggedInLive.live);
      window.removeEventListener('lmn:checkAuth:successful', userLoggedInLive.live);
      window.removeEventListener('lmn:logout:successful', handleUserLoggedOut);
      window.removeEventListener('lmn:user:locale:update', handleUserChangedLanguage);
    };
  }, []);

  useEffect(() => {
    if (canUseDOM) {
      if (searchPageData?.productsData?.apiStatus === APIStatusEnum.WAIT) {
        const searchParamsInitial = getSearchParams() as SearchParams;
        waitingCall({
          searchParamsInitial,
          commonData,
          localization,
          handleWaitingFinished: pageWrapperClientSearch.handleWaitingFinishedIfCurrent(),
          setAPIStatus: pageWrapperClientSearch.handleSearchAPIStatusChanged,
          curiosityParams,
          isNewSearch: true,
        });
      }
    }
  }, [canUseDOM]);

  stepsRendered.add(pageType);

  let content;
  const updatedHotelDetailData: BrandedProps = {
    ...hotelDetailData,
    initialParams: {
      ...hotelDetailData.initialParams,
      selectedMealPlanId: mealPlanIdRef.current,
      selectedMealPlanCode: mealPlanCodeRef.current,
      selectedBrandedFareId: brandedFareIdRef.current,
    },
  };
  // MPH-935: the Evo flow never routes Hotel Only to the flight step, but the URL stays reachable by a
  // shared link, a bookmark or history from a session in the other arm — and with no transport on the
  // offers response that page renders a permanent spinner. Falling through to the hotel-detail layout
  // keeps the guard on the client, where evolutionEnabled is resolvable: the middleware cannot resolve it
  // and would have to reject the URL for the control arm too, which must stay exactly as it is today.
  //
  // Known and accepted: the page type itself is not rewritten, so this visit still reports as the flight
  // step. Hotjar tags it EVO_FLIGHT, the tab takes the generic title rather than the hotel's, the sticky
  // bar's bottom padding is not applied and Biff does not mount — all of which key off HOTEL_DETAIL.
  // Correcting it means rewriting the page type under a rendered page, which is load-bearing across the
  // whole funnel; not worth that risk for a URL only reachable from a stale link. (MPH-935)
  const evoHotelOnlyFlightStep = pageType === PAGE_TYPE.TRANSPORT_DETAIL && !!evolutionEnabled && accommodationOnly;
  if (pageType === PAGE_TYPE.HOTEL_DETAIL || evoHotelOnlyFlightStep) {
    content = (
      <ErrorBoundary id="PageWrapperClient/HOTEL_DETAIL">
        <BrandedPage {...updatedHotelDetailData}>
          {evolutionEnabled ? <EvoDetailsLayout /> : <HotelAndRoomsLayout {...updatedHotelDetailData} />}
        </BrandedPage>
      </ErrorBoundary>
    );
  } else if (pageType === PAGE_TYPE.TRANSPORT_DETAIL) {
    content = (
      <ErrorBoundary id="PageWrapperClient/TRANSPORT_DETAIL">
        <BrandedPage {...updatedHotelDetailData}>
          {evolutionEnabled ? <EvoTransportLayout /> : <TransportLayout />}
        </BrandedPage>
      </ErrorBoundary>
    );
  } else if (pageType === PAGE_TYPE.REVIEW) {
    content = (
      <ErrorBoundary id="PageWrapperClient/REVIEW">
        <BrandedPage {...updatedHotelDetailData}>
          <PackageReviewLayout />
        </BrandedPage>
      </ErrorBoundary>
    );
  } else {
    content = (
      <ErrorBoundary id="PageWrapperClient/SEARCH_MAIN">
        <SearchMain
          mapRef={mapRef}
          handleIsLoadingChanged={pageWrapperClientSearch.handleIsLoadingChanged}
          handleDynamicFiltersResponseChanged={pageWrapperClientSearch.handleDynamicFiltersResponseChanged}
          handleFiltersGroupsChanged={pageWrapperClientSearch.handleFiltersGroupsChanged}
        />
      </ErrorBoundary>
    );
  }

  const commonClient: CommonClient = {
    reportListView,
    isMobile,
    isDesktop,
    isTablet,
    isEvoLayout: isEvoStep,
    isEvoResultsLayout: isEvoResultsStep,
    handleSearchChanged: pageWrapperClientSearch.handleSearchChanged,
    // Not the handler itself: the CSW caches whatever it is given on its first render, which may belong
    // to a render React never committed. handleNewSearchLive always calls the mounted one.
    handleNewSearch: handleNewSearchLive,
    handlePagination: pageWrapperClientSearch.handlePagination,
    handleBackToResults: pageWrapperClientDetails.handleBackToResults,
    handleChangeTransport: pageWrapperClientDetails.handleChangeTransport,
    handleChangeRoom: pageWrapperClientDetails.handleChangeRoom,
    handleChangeStay: pageWrapperClientDetails.handleChangeStay,
    handleTransportChosen: pageWrapperClientDetails.handleTransportChosen,
    handleFlightClicked: pageWrapperClientDetails.handleFlightClicked,
    handleHotelChosen: pageWrapperClientDetails.handleHotelChosen,
    handleRoomChosen: pageWrapperClientDetails.handleRoomChosen,
    handleSearchAPIStatusChanged: pageWrapperClientSearch.handleSearchAPIStatusChanged,
    handleSearchProductsDataChanged: pageWrapperClientSearch.handleSearchProductsDataChanged,
    handleRefreshSearchFromHotelDetails: pageWrapperClientDetails.handleRefreshSearchFromHotelDetails,
    handleDynamicFiltersResponseChanged: pageWrapperClientSearch.handleDynamicFiltersResponseChanged,
    handleEvoGoToCheckout: pageWrapperClientDetails.handleEvoGoToCheckout,
    handleEvoGoToChangeTransport: pageWrapperClientDetails.handleEvoGoToChangeTransport,
    handleEvoDefaultTransportShown: pageWrapperClientDetails.handleEvoDefaultTransportShown,
    handleEvoGoToHotelDetails: pageWrapperClientDetails.handleEvoGoToHotelDetails,
    starTrack,
    // Same reason as handleNewSearch: the live setter always reaches the mounted instance.
    setDefaultValuesCSW: setDefaultValuesCSWLive,
    externalHeaderEnabled,
  };

  useEffect(() => {
    // MPH-1159: DETAIL_PAGE_TYPES counts the review as a detail step, but it has no CSW to seed —
    // and seeding it would re-render the whole page mid-quotation. The next step re-runs this.
    if (!showsCsw(pageType)) return;
    const { departureIntervals } = createSearchParamsFromUrl();
    const currentTabId: SupportedTabId = getCSWTabId(searchMode);
    const isDetailsPage = departureIntervals && DETAIL_PAGE_TYPES.includes(pageType);
    const isLiveSearch = isLiveSearchByDate(pageWrapperData.searchParams[URLParameters.DATE_FROM]);
    if (!isDetailsPage && !isLiveSearch) return;
    startTransition(() => {
      setDefaultValuesCSW((prevDefaultValues) => ({
        ...prevDefaultValues,
        [currentTabId]: {
          ...prevDefaultValues[currentTabId],
          dates: {
            ...prevDefaultValues[currentTabId].dates,
            ...(isDetailsPage ? { calendar: getCalendar(departureIntervals) } : {}),
            mode: 'calendar',
          },
        },
      }));
    });
  }, [pageType, pageWrapperData.searchParams[URLParameters.DATE_FROM], searchMode]);

  const cswProps: CSWProps = useMemo(
    () => ({
      defaultValues: defaultValuesCSW,
      hotelId,
      ssrUtils: ssrUtilsCSW,
    }),
    [defaultValuesCSW, hotelId, ssrUtilsCSW]
  );

  return (
    <ErrorBoundary id="PageWrapperClient">
      <VersionGuard commonData={commonData} />
      <BiffBootstrap isDev={curiosityParams.isDev} isLocal={curiosityParams.isLocal} />
      {isDebug && <Script id="debug">{`var debugData = ${JSON.stringify(debugData)}`}</Script>}
      <StyleSheetManagerProvider>
        <PageWrapperProvider pageWrapperData={updatedPageWrapperData} pageType={pageType}>
          <CuriosityManagerProvider
            commonData={commonData}
            localization={localization}
            curiosityParams={curiosityParams}
          >
            <CommonClientProvider commonClient={commonClient}>
              <PriceModeProvider initialPreference={commonData.totalPricePreference}>
                <SearchDefaultDataProvider searchDefaultData={searchDefaultData}>
                  <ClientSideCasperCookieSetter casperResponseCookies={commonData.casperResponseCookies} />
                  <NavigationManager
                    evolutionResultsOnlyEnabled={isEvoStep}
                    isApp={isApp}
                    appShowCsw={appShowCsw}
                    externalHeaderEnabled={externalHeaderEnabled}
                    staticHeader={staticHeader}
                    brand={brand}
                    showGlobalLoadLine={showGlobalLoadLine}
                    showNavigation={showNavigation}
                    pageType={pageType}
                    handleGoToNextStep={handleGoToNextStep}
                    headerProps={{
                      externalHeaderEnabled,
                      externalHeaderHTML,
                      appShowCsw,
                      brand,
                      contentHubHeaderProps,
                      cswProps,
                      headerFooterStaticEnabled,
                    }}
                    searchPageData={searchPageData}
                  >
                    {content}
                    <DebugUserIdLog />
                  </NavigationManager>
                  {pageType !== PAGE_TYPE.REVIEW && (
                    <TsxContentHubFooter
                      id="content-hub-footer"
                      css={pageType === PAGE_TYPE.HOTEL_DETAIL ? `padding-bottom: 72px;` : undefined}
                      style={{ fontFamily: UBUNTU_FONT_STACK }}
                      evolutionEnabled={isEvoStep}
                    >
                      {contentHubFooterProps && <ContentHubFooter {...contentHubFooterProps} />}
                    </TsxContentHubFooter>
                  )}
                </SearchDefaultDataProvider>
              </PriceModeProvider>
            </CommonClientProvider>
          </CuriosityManagerProvider>
        </PageWrapperProvider>
      </StyleSheetManagerProvider>
    </ErrorBoundary>
  );
};
